<template>
  <Drawer
    title="查询"
    custom-class="page-query-drawer-search"
    :visible="showDrawer"
    :modal="modal"
    :append-to-body="appendToBody"
    :size="size"
    @close="close"
    @closed="closed"
    @open="open"
    :before-close="onCancel"
  >
    <div class="page-query-drawer-search__content">
      <div class="page-query-drawer-search__header">
        <span class="page-query-drawer-search__header-title">已保存的查询：</span>
        <PgSelect
          class="page-query-drawer-search__header-menu"
          ref="headerMenuRef"
          popper-class="page-query-drawer-search__header-option"
          filterable
          :valueIsObj="false"
          valueKey="id"
          :source="selectionMenuUrl"
          :sourceParams="sourceParams"
          @change="menuChange"
          v-model="menuData"
        >
          <template #default="{ options, valueKey, labelKey }">
            <Option
              class="page-query-drawer-search__header-option-item"
              v-for="option in options"
              :key="option[valueKey]"
              :label="option[labelKey]"
              :value="option[valueKey]"
            >
              <span class="page-query-drawer-search__header-option-text">{{
                option[labelKey]
              }}</span>
              <i
                class="page-query-drawer-search__header-option-icon el-icon-close"
                @click.stop="deleteCriteriaMenu(option[valueKey])"
              ></i>
            </Option>
          </template>
        </PgSelect>
        <Link type="warning" :underline="false" @click="restFormData">重置条件</Link>
      </div>
      <PgForm
        class="page-query-drawer-search__form"
        :formItems="formItems"
        :model="formData"
      ></PgForm>
      <div class="page-query-drawer-search__button">
        <Link
          type="warning"
          :underline="false"
          class="page-query-drawer-search__button-link"
          @click="saveQuery"
          >保存查询条件</Link
        >
        <ElButton type="primary" round v-if="mode === 'query'" @click="sure">确定</ElButton>
      </div>
    </div>
  </Drawer>
</template>
<script>
export default {
  inheritAttrs: false,
}
</script>
<script setup>
import { shallowRef, ref, toRef, inject, computed } from 'vue'
import { Drawer, Link, Option } from 'element-ui'
import { PgSelect } from '../../select/index'
import { PgForm } from '../../form/index'
import { useDrawerPack, useTemplate } from '../../../hooks/index'
import { get, post, clone, setValueByPath } from '../../../utils/index'
import { BUTTONACTION } from '../../../tokens/index'

const props = defineProps({
  appendToBody: {
    type: Boolean,
    default: true,
  },
  modal: {
    type: Boolean,
    default: true,
  },
  size: {
    type: [Number, String],
    default: 400,
  },
  visible: {
    type: Boolean,
    default: true,
  },
  // 自定义条件保存接口
  saveCriteriaMenuUrl: {
    type: String,
    default: 'rest/ezcomp/dict/criteria/menu/submit',
  },
  // 自定义条件select列表
  selectionMenuUrl: {
    type: String,
    default: 'rest/ezcomp/dict/criteria/menu/options',
  },
  // 删除条件接口
  deleteCriteriaMenuUrl: {
    type: String,
    default: 'rest/ezcomp/dict/criteria/menu/remove',
  },
  // 自定义条件获取表单内容。
  getCriteriaMenuUrl: {
    type: String,
    default: 'rest/ezcomp/dict/criteria/menu/getOfEdit',
  },
  //获取查询条件；当为String时表示单独使用模式，仅保留保存查询条件按钮且不抛出on-ok-and-continue
  queryForm: [String, Object],
  queryData: Object,
  shortcutData: Object,
})

const emit = defineEmits('update:visible', 'on-cancel', 'close', 'on-ok', 'on-ok-and-continue')
const { showDrawer, close, closed, onCancel } = useDrawerPack(toRef(props, 'visible'), emit)

const formData = ref({})
const formItems = shallowRef(null)
const mode = computed(() => (typeof props.queryForm === 'string' ? 'alone' : 'query'))
const open = async () => {
  if (mode.value === 'query') {
    formItems.value = props.queryForm
  } else {
    const [res] = await get(props.queryForm)
    formItems.value = res?.data ?? null
  }
  formData.value = clone(props.queryData) ?? {}
}

const sourceParams = computed(() => ({
  preset: mode.value !== 'query',
  criteria: mode.value === 'query' ? formItems.value?.id : formItems.value.criteria.id,
}))

const sure = () => {
  showDrawer.value = false
  emit('on-ok', { data: formData.value })
}

const menuData = ref('')
const headerMenuRef = ref(null)
const deleteCriteriaMenu = async (id) => {
  const res = await post(props.deleteCriteriaMenuUrl, { id })
  if (res) {
    headerMenuRef.value.filterMethod()
    if (mode.value === 'query') {
      emit('on-ok-and-continue')
    }
  }
}
const menuChange = async () => {
  if (menuData.value) {
    const [res] = await get(props.getCriteriaMenuUrl, { id: menuData.value })
    formData.value = res?.data?.parameter ?? formData.value
  }
}

const { getTemplateData, compileData } = useTemplate()
//重置
const restFormData = () => {
  menuData.value = ''
  const initFormData = {}
  const templateData = getTemplateData()
  for (const item of formItems.value.items) {
    if (item.defaultValue) {
      setValueByPath(initFormData, item.property, compileData(item.defaultValue, templateData))
    }
  }
  formData.value = initFormData
}

/***
 * 保存查询条件
 ***/
const buttonAction = inject(BUTTONACTION)
//保存
const saveQuery = async () => {
  const res = await buttonAction({
    id: 'search_save_query',
    doing: {
      action: { code: 'component' },
      property: {
        component: 'PgDialogForm',
      },
      parameter: {
        title: '保存查询条件',
        submitUrl: props.saveCriteriaMenuUrl,
        formModel: {
          criteria: mode.value === 'query' ? formItems.value?.id : formItems.value.criteria.id,
          id: null,
          parameter: { ...props.shortcutData, ...formData.value },
          visible: null,
        },
        formItems: {
          form: {
            property: {
              formGroup: false,
              inline: false,
            },
          },
          items: [
            {
              name: '标题',
              property: 'name',
              componentCode: 'PgInput',
              parameter: {
                placeholder: '请输入查询标题',
              },
              style: {
                marginBottom: '15px',
              },
              visible: true,
            },
            {
              name: '位置',
              property: 'position',
              componentCode: 'PgInput',
              parameter: {
                placeholder: '请输入位置',
              },
              style: {
                marginBottom: '15px',
              },
              visible: true,
            },
            {
              name: '显示在菜单栏',
              property: 'visible',
              componentCode: 'PgCheckbox',
              parameter: {
                text: '显示在菜单栏',
              },
              style: {
                marginBottom: '15px',
                fontSize: '12px',
              },
              visible: mode.value === 'query' ? true : false,
            },
          ],
        },
      },
    },
  })
  if (res) {
    headerMenuRef.value.filterMethod()
    if (mode.value === 'query') {
      emit('on-ok-and-continue')
    }
  }
}
</script>
<style lang="scss">
@import '../style/page-query-drawer-search.scss';
</style>
